// src/features/portfolio-optimization/components/ConvictionTable.tsx
//
// Zone 3 — the ML "Glass Box" (The Quant Lab).
//
// The Weights & Conviction table: one row per active Black-Litterman view,
// exposing the raw ML diagnostics the optimiser consumed — calibrated
// master probability (rendered as a HEATMAP cell so the eye lands on the
// highest-conviction bets), the view return Q, the view uncertainty Omega
// and the Phase-1 z-score. Clicking a row opens the per-asset tearsheet.
//
// We deliberately hand-roll a styled <table> rather than pull in AG Grid:
// recharts is already vendored, the dataset is small (one cross-section) and
// this keeps the bundle lean and the heatmap fully theme-aware.

import type { MlInsightSignal } from '../api';

interface ConvictionTableProps {
    signals: MlInsightSignal[];
    selectedSymbol: string | null;
    onSelect: (symbol: string) => void;
}

// Map a calibrated probability to a heat colour. 0.50 = neutral (slate);
// above leans green (conviction the side is right), below leans red.
const probHeat = (p: number): string => {
    const edge = Math.max(-1, Math.min(1, (p - 0.5) / 0.25)); // ±0.25 -> ±1
    if (edge >= 0) {
        const a = 0.12 + edge * 0.5;
        return `rgba(16, 185, 129, ${a.toFixed(3)})`; // emerald
    }
    const a = 0.12 + Math.abs(edge) * 0.5;
    return `rgba(244, 63, 94, ${a.toFixed(3)})`; // rose
};

const th =
    'px-4 py-3 font-semibold text-slate-400 dark:text-slate-500 uppercase text-xs tracking-wider';

export const ConvictionTable = ({
    signals,
    selectedSymbol,
    onSelect,
}: ConvictionTableProps) => {
    if (!signals.length) {
        return (
            <div className="p-10 text-center muted">
                No active ML views for this date.
            </div>
        );
    }

    return (
        <div className="overflow-y-auto max-h-[560px]">
            <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-white dark:bg-slate-800 sticky top-0 z-10 shadow-sm border-b border-slate-200 dark:border-slate-700">
                    <tr>
                        <th className={th}>Symbol</th>
                        <th className={th}>Side</th>
                        <th className={`${th} text-center`}>Conviction (p)</th>
                        <th className={`${th} text-right`}>Expected Return</th>
                        <th className={`${th} text-right`}>Uncertainty Ω</th>
                        <th className={`${th} text-right`}>Phase-1 Z</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                    {signals.map((s) => {
                        const isLong = s.ml_side >= 0;
                        const selected = s.symbol === selectedSymbol;
                        return (
                            <tr
                                key={s.symbol}
                                onClick={() => onSelect(s.symbol)}
                                className={`cursor-pointer transition-colors ${
                                    selected
                                        ? 'bg-indigo-50 dark:bg-indigo-900/20'
                                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'
                                }`}
                            >
                                <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                                    {s.symbol}
                                </td>
                                <td className="px-4 py-3">
                                    <span
                                        className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border ${
                                            isLong
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800'
                                                : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800'
                                        }`}
                                    >
                                        {isLong ? 'LONG' : 'SHORT'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <span
                                        className="inline-block min-w-[3.5rem] px-2 py-1 rounded font-mono font-bold text-slate-800 dark:text-slate-100"
                                        style={{ backgroundColor: probHeat(s.master_probability) }}
                                    >
                                        {s.master_probability.toFixed(2)}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-slate-600 dark:text-slate-300">
                                    {(s.expected_return_q * 100).toFixed(2)}%
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-slate-500 dark:text-slate-400">
                                    {s.uncertainty_omega.toFixed(4)}
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-slate-600 dark:text-slate-300">
                                    {s.phase_1_z_score !== null ? (
                                        <span
                                            className={
                                                s.phase_1_z_score >= 0
                                                    ? 'text-emerald-600 dark:text-emerald-400'
                                                    : 'text-rose-600 dark:text-rose-400'
                                            }
                                        >
                                            {s.phase_1_z_score > 0 ? '+' : ''}
                                            {s.phase_1_z_score.toFixed(2)}σ
                                        </span>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ConvictionTable;
